import { useEffect, useState } from "react";
import { RefreshCw, Square } from "lucide-react";
import { Button } from "@web/components/ui/button.tsx";
import { useCancelSync, useStartSync, useSyncRun } from "@web/features/library/api.ts";
import { cn } from "@web/lib/utils.ts";

function formatEta(sec: number): string {
  if (sec < 45) return `~${Math.max(5, sec)}s`;
  const m = Math.round(sec / 60);
  if (m < 60) return `~${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `~${h}h ${rm}m` : `~${h}h`;
}

/** Compact ETA from elapsed wall time × remaining percent. */
function etaSeconds(
  startedAt: string | undefined,
  percent: number,
  now: number,
): number | null {
  if (!startedAt || percent < 4 || percent >= 100) return null;
  const elapsedMs = now - Date.parse(startedAt);
  if (!Number.isFinite(elapsedMs) || elapsedMs < 4_000) return null;
  const remaining = ((elapsedMs / percent) * (100 - percent)) / 1000;
  if (!Number.isFinite(remaining) || remaining < 0) return null;
  return Math.round(remaining);
}

/**
 * Sidebar Sync control for every user — idle starts a full sync; while running,
 * click again to stop (soft-cancel between Spotify pages). Start again to resume
 * from the top (unchanged playlist snapshots are skipped).
 */
export function SyncLibraryControl({ collapsed }: { collapsed: boolean }) {
  const startSync = useStartSync();
  const cancelSync = useCancelSync();
  const { data: syncData, refetch: refetchSync } = useSyncRun({
    refetchInterval: 4_000,
  });
  const [now, setNow] = useState(() => Date.now());

  const run = syncData?.run ?? null;
  const progress = syncData?.progress ?? null;
  const running = run?.status === "running" || startSync.isPending;
  const stopping = cancelSync.isPending;

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
      void refetchSync();
    }, 1_000);
    return () => window.clearInterval(id);
  }, [running, refetchSync]);

  const percent = progress?.percent ?? 0;
  const eta = running ? etaSeconds(run?.started_at, percent, now) : null;
  const detail = progress?.detail ?? (startSync.isPending ? "Starting…" : null);
  const label = progress?.label ?? "Syncing…";

  function onToggle() {
    if (stopping || startSync.isPending) return;
    if (running) {
      cancelSync.mutate();
      return;
    }
    startSync.mutate({ scope: "full" });
  }

  const title = running
    ? [
        stopping ? "Stopping…" : "Click to stop",
        label,
        detail,
        percent > 0 ? `${percent}%` : null,
        eta != null ? `${formatEta(eta)} left` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "Pull playlists, then liked songs, into this account";

  if (collapsed) {
    return (
      <Button
        variant="ghost"
        size="icon-sm"
        disabled={stopping || startSync.isPending}
        onClick={onToggle}
        aria-label={running ? "Stop library sync" : "Sync library from Spotify"}
        title={title}
        className="relative"
      >
        {running ? (
          <Square className={cn("size-3.5 fill-current", stopping ? "text-muted" : "text-accent")} />
        ) : (
          <RefreshCw className="size-4" />
        )}
        {running && percent > 0 ? (
          <span className="absolute -right-0.5 -bottom-0.5 rounded bg-panel px-0.5 text-xs font-semibold leading-none tabular-nums text-accent">
            {percent}
          </span>
        ) : null}
      </Button>
    );
  }

  if (running) {
    return (
      <button
        type="button"
        className={cn(
          "w-full space-y-1.5 rounded-md px-1 py-1 text-left transition-colors",
          "hover:bg-row-hover focus-visible:bg-row-hover focus-visible:outline-none",
          stopping && "opacity-70",
        )}
        title={title}
        disabled={stopping}
        onClick={onToggle}
        aria-label="Stop library sync"
      >
        <div className="flex items-center gap-2 text-sm text-text">
          {stopping ? (
            <Square className="size-3.5 shrink-0 fill-current text-muted" aria-hidden />
          ) : (
            <RefreshCw className="size-3.5 shrink-0 animate-spin text-accent" aria-hidden />
          )}
          <span className="min-w-0 flex-1 truncate font-medium">
            {stopping ? "Stopping…" : percent > 0 ? `${percent}%` : "Syncing"}
            {!stopping && eta != null ? (
              <span className="font-normal text-muted"> · {formatEta(eta)}</span>
            ) : null}
          </span>
          {!stopping ? (
            <span className="shrink-0 text-xs font-normal text-muted">Stop</span>
          ) : null}
        </div>
        <div
          className="h-1 w-full overflow-hidden rounded-full bg-panel-elevated"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label}
        >
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-500 ease-out"
            style={{ width: `${Math.max(percent > 0 ? 4 : 8, percent)}%` }}
          />
        </div>
        {detail ? (
          <p className="truncate text-xs leading-tight text-muted tabular-nums">{detail}</p>
        ) : null}
      </button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="w-full justify-start text-muted"
      disabled={startSync.isPending}
      onClick={onToggle}
      aria-label="Sync library from Spotify"
      title={title}
    >
      <RefreshCw className="size-3.5" />
      Sync library
    </Button>
  );
}
